import React, { useState, useEffect } from 'react';
import Footer from '../components/Footer';
import GoogleLoginButton from '../components/GoogleLoginButton';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { authAPI } from '../services/apiService';
import axios from 'axios';

const Signup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    password: '',
    payoutMethod: 'Bank Transfer',
    agreeTerms: false,
    referredBy: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [referrerInfo, setReferrerInfo] = useState(null);
  const [verifyingReferral, setVerifyingReferral] = useState(false);

  useEffect(() => {
    // Check if there's a referral code in the URL
    const refCode = searchParams.get('ref');
    if (refCode) {
      setFormData(prev => ({ ...prev, referredBy: refCode }));
      verifyReferralCode(refCode);
    }
  }, [searchParams]);

  const verifyReferralCode = async (code) => {
    setVerifyingReferral(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.get(`${backendUrl}/api/verify-referral/${code}`);
      setReferrerInfo(response.data);
      toast.success(`Referred by ${response.data.referrerName}!`);
    } catch (error) {
      console.error('Invalid referral code:', error);
      toast.error('Invalid referral code. You can still sign up without it.');
      setFormData(prev => ({ ...prev, referredBy: '' }));
    } finally {
      setVerifyingReferral(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : value 
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.agreeTerms) {
      toast.error('Please agree to the Terms & Privacy Policy');
      return;
    }

    setIsLoading(true);

    try {
      const data = await authAPI.signup({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        mobile: formData.mobile,
        password: formData.password,
        payoutMethod: formData.payoutMethod,
        referredBy: formData.referredBy || null
      });
      
      // Store token and auth state
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('isAuthenticated', 'true');
      
      // Fetch and store user data
      const userData = await authAPI.getCurrentUser();
      localStorage.setItem('user', JSON.stringify(userData));
      
      toast.success('Account created successfully!');
      
      // Force page reload to update Navbar
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Signup error:', error);
      toast.error(error.response?.data?.detail || 'Signup failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-6">
      <Card className="w-full max-w-2xl shadow-2xl border-4 border-red-400 bg-white/90 backdrop-blur-sm">
        <CardHeader className="text-center">
          <CardTitle className="text-3xl font-bold text-gray-900">Create Your Free Account</CardTitle>
          <CardDescription className="text-gray-600">Start earning commissions in minutes</CardDescription>
          
          {/* Referral Info Banner */}
          {referrerInfo && (
            <div className="mt-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-400 rounded-lg">
              <p className="text-sm font-semibold text-green-800">
                🎉 You're being referred by <span className="font-bold">{referrerInfo.referrerName}</span>!
              </p>
              <p className="text-xs text-green-700 mt-1">
                Complete your signup to help them earn rewards
              </p>
            </div>
          )}
          
          {verifyingReferral && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-300 rounded-lg">
              <p className="text-sm text-blue-700">Verifying referral code...</p>
            </div>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="firstName" className="text-gray-700 font-semibold">First Name</Label>
                <Input
                  id="firstName"
                  name="firstName"
                  placeholder="Rajesh"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  className="h-12"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName" className="text-gray-700 font-semibold">Last Name</Label>
                <Input
                  id="lastName"
                  name="lastName"
                  placeholder="Kumar"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  className="h-12"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-700 font-semibold">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="your.email@example.com"
                value={formData.email}
                onChange={handleChange}
                required
                className="h-12"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="mobile" className="text-gray-700 font-semibold">Mobile Number</Label>
              <Input
                id="mobile"
                name="mobile"
                type="tel"
                placeholder="+91 9876543210"
                value={formData.mobile}
                onChange={handleChange}
                className="h-12"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-700 font-semibold">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="Create a strong password"
                value={formData.password}
                onChange={handleChange}
                required
                className="h-12"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="payoutMethod" className="text-gray-700 font-semibold">Preferred Payout Method</Label>
              <Select 
                value={formData.payoutMethod} 
                onValueChange={(value) => setFormData({ ...formData, payoutMethod: value })}
              >
                <SelectTrigger className="h-12">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank">Bank Transfer</SelectItem>
                  <SelectItem value="upi">UPI / Paytm</SelectItem>
                  <SelectItem value="paypal">PayPal</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-start space-x-3">
              <Checkbox 
                id="agreeTerms"
                name="agreeTerms"
                checked={formData.agreeTerms}
                onCheckedChange={(checked) => setFormData({ ...formData, agreeTerms: checked })}
              />
              <Label htmlFor="agreeTerms" className="text-sm text-gray-600 leading-relaxed cursor-pointer">
                I agree to the Terms & Privacy Policy
              </Label>
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 text-lg font-bold text-white border-2 border-yellow-400 shadow-lg" 
              style={{ background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 100%)' }}
              disabled={isLoading}
            >
              {isLoading ? 'Creating Account...' : 'Create account & get link'}
            </Button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-white px-2 text-gray-500">Or continue with</span>
              </div>
            </div>
            
            <div className="mt-4">
              <GoogleLoginButton redirectPath="/dashboard" />
            </div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Already have an account?{' '}
              <button 
                onClick={() => navigate('/login')}
                className="text-red-600 hover:text-red-700 font-semibold hover:underline"
              >
                Login
              </button>
            </p>
          </div>
        </CardContent>
      </Card>
      <Footer />
    </div>
  );
};

export default Signup;
